<script setup lang="ts">
import AppContextPanel from '@/components/app-shell/AppContextPanel.vue'
import CompanyCreateDialog from '@/components/companies/CompanyCreateDialog.vue'
import FinancingRequestConditionsStep from '@/components/financing-requests/FinancingRequestConditionsStep.vue'
import FinancingRequestCreateDone from '@/components/financing-requests/FinancingRequestCreateDone.vue'
import FinancingRequestDocumentsCard from '@/components/financing-requests/FinancingRequestDocumentsCard.vue'
import FinancingRequestFinancingStep from '@/components/financing-requests/FinancingRequestFinancingStep.vue'
import FinancingRequestOriginStep from '@/components/financing-requests/FinancingRequestOriginStep.vue'
import FinancingRequestUnitsStep from '@/components/financing-requests/FinancingRequestUnitsStep.vue'
import {
  emptyFinancingDraft,
  isFinancingComplete,
  modernizationPayload,
} from '@/components/financing-requests/financingDraft'
import FinestButton from '@/components/FinestButton.vue'
import PersonOrCompanyStep, { type PartyOption } from '@/components/PersonOrCompanyStep.vue'
import FinestStepNav from '@/components/FinestStepNav.vue'
import PropertyCreateAssistant from '@/components/portfolio/PropertyCreateAssistant.vue'
import PropertyLocationStep from '@/components/portfolio/PropertyLocationStep.vue'
import PropertyTypeStep from '@/components/portfolio/PropertyTypeStep.vue'
import { usePropertySpaceSize } from '@/pages/investors/portfolio/usePropertySpaceSize'
import type { ExtendedEnum } from '@/types'
import { estateObjectAddressLine } from '@/utils/estate-object'
import FinancingRequestWizardController from '@controllers/Investors/FinancingRequestWizardController'
import ProjectsController from '@controllers/Investors/ProjectsController'
import { Head, router, useForm } from '@inertiajs/vue3'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDefaultPurchaseCosts, type PurchaseCostDefaults } from './useDefaultPurchaseCosts'
import { useFinancingRequestCreateSteps, type FinancingOrigin, type ResumeStep } from './useFinancingRequestCreateSteps'
import { usePropertyRents } from './usePropertyRents'
import Bundesland = App.Enums.Bundesland
import PersonType = App.Enums.PersonType
import EstateObjectData = App.Data.EstateObject.EstateObjectData
import PurchaseDocumentData = App.Data.Documents.PurchaseDocumentData
import RequiredDocumentData = App.Data.Documents.RequiredDocumentData
import EstateObjectType = App.Enums.EstateObject.EstateObjectType
import LoanType = App.Enums.FinancingRequest.LoanType
import RealEstateImprovement = App.Enums.RealEstateImprovement

const props = defineProps<{
  project: { id: string; title: string | null } | null
  initialOrigin: FinancingOrigin | null
  financedEstateObject: EstateObjectData | null
  resumeStep: ResumeStep
  buyerDocuments: Record<string, RequiredDocumentData[]>
  purchaseDocuments: PurchaseDocumentData[]
  portfolioObjectDocuments: RequiredDocumentData[] | null
  portfolioObjects: EstateObjectData[]
  buyerOptions: PartyOption[]
  estateObjectTypes: (ExtendedEnum<EstateObjectType> & { has_living_space: boolean })[]
  improvementTypes: RealEstateImprovement[]
  loanTypes: LoanType[]
  states: { value: Bundesland; label: string; land_register_charge: number }[]
  purchaseCostDefaults: PurchaseCostDefaults
}>()

const { t } = useI18n()

const form = useForm({
  buyer_type: null as PersonType | null,
  buyer_id: null as string | null,
  estate_object_type: null as EstateObjectType | null,
  google_place_id: null as string | null,
  street: null as string | null,
  house_number: null as string | null,
  address_addition: null as string | null,
  zip_code: null as string | null,
  city: null as string | null,
  state: null as Bundesland | null,
  living_space_size: null as number | null,
  business_space_size: null as number | null,
  number_of_units: null as number | null,
  actual_rent_living: null as number | null,
  actual_rent_business: null as number | null,
  expected_rent_living: null as number | null,
  expected_rent_business: null as number | null,
  financing: emptyFinancingDraft(),
  documents: {} as Record<string, File>,
})

const portfolioForm = useForm({
  estate_object_id: null as string | null,
  loan_type: null as LoanType | null,
  total_financing_amount: null as number | null,
  equity_contribution: null as number | null,
  interest_term_years: null as number | null,
  repayment_rate: null as number | null,
})

const {
  origin,
  step,
  screen,
  locked,
  doneStep,
  stepErrors,
  highlightMissing,
  requestValidation,
  selectOrigin,
  canReturnTo,
  goBackTo,
  advance,
  showFirstRejectedStep,
} = useFinancingRequestCreateSteps(
  () => props.resumeStep,
  () => props.project?.id,
  () => ({ ...portfolioForm.errors, ...form.errors }),
  () => props.initialOrigin,
)

const hasLivingSpace = computed(
  () => props.estateObjectTypes.find((option) => option.id === form.estate_object_type)?.has_living_space ?? true,
)

const spaceLabel = computed(() =>
  hasLivingSpace.value ? t('questions.living_space_size') : t('questions.business_space_size'),
)

const spaceFieldLabel = computed(() =>
  hasLivingSpace.value ? t('fields.living_space_size') : t('fields.business_space_size'),
)

const spaceSize = usePropertySpaceSize(form, hasLivingSpace)

const { actualRent, expectedRent } = usePropertyRents(form, hasLivingSpace)

const landRegisterCharge = computed(
  () => props.states.find((option) => option.value === form.state)?.land_register_charge ?? null,
)

useDefaultPurchaseCosts(form.financing, () => landRegisterCharge.value, props.purchaseCostDefaults)

const stepLabels = computed(() =>
  origin.value === 'portfolio'
    ? [t('steps.origin'), t('steps.financing'), t('steps.documents'), t('steps.done')]
    : [
        t('steps.origin'),
        t('steps.borrower'),
        t('steps.type'),
        t('steps.location'),
        t('steps.units'),
        t('steps.financing'),
        t('steps.documents'),
        t('steps.done'),
      ],
)

const locationComplete = computed(
  () => !!form.street && !!form.house_number && !!form.zip_code && !!form.city && !!form.state,
)

const unitsComplete = computed(
  () => spaceSize.value !== null && form.number_of_units !== null && actualRent.value !== null,
)

const selectedPortfolioObject = computed(
  () => props.portfolioObjects.find((object) => object.id === portfolioForm.estate_object_id) ?? null,
)

const loadingPortfolioDocuments = ref(false)

const loadPortfolioDocuments = (): void => {
  loadingPortfolioDocuments.value = true

  router.reload({
    only: ['portfolioObjectDocuments'],
    data: { estate_object_id: portfolioForm.estate_object_id },
    onFinish: () => {
      loadingPortfolioDocuments.value = false
    },
  })
}

watch(screen, (current) => {
  if (current === 'documents' && origin.value === 'portfolio') {
    loadPortfolioDocuments()
  }
})

const portfolioDocumentsReady = computed(
  () => !loadingPortfolioDocuments.value && props.portfolioObjectDocuments !== null,
)

const attachedRequirements = computed(() => {
  if (origin.value === 'portfolio') {
    return props.portfolioObjectDocuments ?? []
  }

  return form.buyer_id ? (props.buyerDocuments[form.buyer_id] ?? []) : []
})

const purchaseRequirements = computed(() => (origin.value === 'portfolio' ? [] : props.purchaseDocuments))

const documentsComplete = computed(
  () =>
    (origin.value !== 'portfolio' || portfolioDocumentsReady.value) &&
    attachedRequirements.value.every((requirement) => requirement.documents.length) &&
    purchaseRequirements.value.every((document) => form.documents[document.documentType]),
)

const conditionsComplete = computed(
  () =>
    portfolioForm.loan_type !== null &&
    portfolioForm.total_financing_amount !== null &&
    portfolioForm.repayment_rate !== null &&
    (portfolioForm.loan_type === 'variable' || portfolioForm.interest_term_years !== null),
)

const canLeaveStep = computed(() => {
  switch (screen.value) {
    case 'location':
      return locationComplete.value
    case 'units':
      return unitsComplete.value
    case 'financing':
      return isFinancingComplete(form.financing)
    case 'conditions':
      return conditionsComplete.value
    case 'documents':
      return documentsComplete.value
    default:
      return true
  }
})

/** Steps that carry a single choice commit it on click, so they need no forward button. */
const autoAdvancing = computed(() => ['origin', 'borrower', 'type'].includes(screen.value))

const trackedFields = computed(() => [
  { label: t('fields.estate_object_type'), filled: !!form.estate_object_type },
  { label: t('fields.street'), filled: !!form.street },
  { label: t('fields.house_number'), filled: !!form.house_number },
  { label: t('fields.zip_code'), filled: !!form.zip_code },
  { label: t('fields.city'), filled: !!form.city },
  { label: t('fields.state'), filled: !!form.state },
  { label: spaceFieldLabel.value, filled: spaceSize.value !== null },
  { label: t('fields.number_of_units'), filled: form.number_of_units !== null },
  { label: t('fields.actual_rent'), filled: actualRent.value !== null },
  { label: t('fields.purchase_price'), filled: form.financing.purchase_price !== null },
  { label: t('fields.equity'), filled: form.financing.equity !== null },
  { label: t('fields.loan_type'), filled: form.financing.loan_type !== null },
  { label: t('fields.total_financing_amount'), filled: form.financing.total_financing_amount !== null },
])

const showAssistant = computed(
  () => origin.value === 'new' && ['type', 'location', 'units', 'financing'].includes(screen.value),
)

const chooseOrigin = (next: FinancingOrigin): void => {
  selectOrigin(next)

  if (next === 'new') {
    advance()
  }
}

const selectBorrower = (option: PartyOption): void => {
  form.buyer_id = option.id
  form.buyer_type = option.type

  advance()
}

const showCompanyDialog = ref(false)

const selectCreatedCompany = (companyId: string): void => {
  form.buyer_id = companyId
  form.buyer_type = 'company'

  advance()
}

/** A recorded property needs no capturing — only the conditions the request is asking for. */
const selectPortfolioObject = (estateObject: EstateObjectData): void => {
  portfolioForm.estate_object_id = estateObject.id ?? ''

  advance()
}

const submitPortfolioRequest = (): void => {
  portfolioForm.post(FinancingRequestWizardController.storeWithPortfolioObject().url, {
    preserveScroll: true,
    onError: showFirstRejectedStep,
  })
}

const submit = (): void => {
  form
    .transform(({ financing, ...rest }) => ({
      ...rest,
      ...financing,
      modernizations: modernizationPayload(financing.modernizations),
    }))
    .post(FinancingRequestWizardController.storeWithNewProperty().url, {
      preserveScroll: true,
      onError: showFirstRejectedStep,
    })
}

const next = (): void => {
  if (!canLeaveStep.value) {
    requestValidation()

    return
  }

  if (screen.value === 'documents') {
    if (origin.value === 'portfolio') {
      submitPortfolioRequest()
    } else {
      submit()
    }

    return
  }

  advance()
}

const doneTitle = computed(
  () => props.project?.title || (props.financedEstateObject ? estateObjectAddressLine(props.financedEstateObject) : ''),
)

const doneSummary = computed(() => {
  const estateObject = props.financedEstateObject
  const buyer = props.buyerOptions.find((option) => option.id === estateObject?.owner_id)

  return [
    { label: t('summary.property'), value: doneTitle.value },
    {
      label: t('summary.type'),
      value: estateObject?.estate_object_type
        ? t(`enums.estateObjectType.${estateObject.estate_object_type}`)
        : t('empty'),
    },
    { label: t('summary.borrower'), value: buyer?.name ?? t('empty') },
  ]
})
</script>

<template>
  <Head :title="t('heading')" />

  <AppContextPanel v-if="showAssistant">
    <PropertyCreateAssistant :fields="trackedFields" />
  </AppContextPanel>

  <div class="mb-4">
    <Link :href="ProjectsController.index()">
      <FinestButton :label="t('back')" icon="pi pi-arrow-left" variant="ghost" size="sm" />
    </Link>
  </div>

  <div class="mb-6">
    <p class="section-label">{{ t('eyebrow') }}</p>
    <div class="flex items-center gap-2">
      <h1 class="page-title">{{ t('heading') }}</h1>
      <PageTitleHelp :text="t('subtitle')" />
    </div>
  </div>

  <FinestStepNav :steps="stepLabels" :current="step" :locked="locked" @select="goBackTo" />

  <FinancingRequestOriginStep
    v-if="screen === 'origin'"
    :origin="origin"
    :portfolio-objects="portfolioObjects"
    :selected-id="portfolioForm.estate_object_id"
    :errors="stepErrors"
    @update:origin="chooseOrigin"
    @select="selectPortfolioObject" />

  <PersonOrCompanyStep
    v-else-if="screen === 'borrower'"
    role="borrower"
    :options="buyerOptions"
    :selected-id="form.buyer_id"
    :errors="stepErrors"
    @select="selectBorrower"
    @add-company="showCompanyDialog = true" />

  <PropertyTypeStep
    v-else-if="screen === 'type'"
    v-model="form.estate_object_type"
    :types="estateObjectTypes"
    :errors="stepErrors"
    @select="advance" />

  <PropertyLocationStep
    v-else-if="screen === 'location'"
    v-model:place-id="form.google_place_id"
    v-model:street="form.street"
    v-model:house-number="form.house_number"
    v-model:address-addition="form.address_addition"
    v-model:zip-code="form.zip_code"
    v-model:city="form.city"
    v-model:state="form.state"
    :states="states"
    :errors="stepErrors"
    :show-missing="highlightMissing" />

  <FinancingRequestUnitsStep
    v-else-if="screen === 'units'"
    v-model:space-size="spaceSize"
    v-model:number-of-units="form.number_of_units"
    v-model:actual-rent="actualRent"
    v-model:expected-rent="expectedRent"
    :space-label="spaceLabel"
    :errors="stepErrors"
    :show-missing="highlightMissing" />

  <FinancingRequestFinancingStep
    v-else-if="screen === 'financing'"
    v-model="form.financing"
    :improvement-types="improvementTypes"
    :loan-types="loanTypes"
    :errors="stepErrors"
    :show-missing="highlightMissing" />

  <FinancingRequestConditionsStep
    v-else-if="screen === 'conditions'"
    v-model:loan-type="portfolioForm.loan_type"
    v-model:total-financing-amount="portfolioForm.total_financing_amount"
    v-model:equity-contribution="portfolioForm.equity_contribution"
    v-model:interest-term-years="portfolioForm.interest_term_years"
    v-model:repayment-rate="portfolioForm.repayment_rate"
    :loan-types="loanTypes"
    :existing-loans="selectedPortfolioObject?.loans ?? []"
    :errors="stepErrors"
    :show-missing="highlightMissing" />

  <div
    v-else-if="screen === 'documents' && origin === 'portfolio' && !portfolioDocumentsReady"
    class="flex justify-center py-10"
    role="status">
    <i class="pi pi-spinner text-action animate-spin text-2xl!" aria-hidden="true" />
    <span class="sr-only">{{ t('loadingDocuments') }}</span>
  </div>

  <FinancingRequestDocumentsCard
    v-else-if="screen === 'documents'"
    v-model:files="form.documents"
    :requirements="attachedRequirements"
    :purchase-requirements="purchaseRequirements"
    :show-missing="highlightMissing" />

  <FinancingRequestCreateDone v-else-if="project" :project-id="project.id" :title="doneTitle" :summary="doneSummary" />

  <div v-if="step !== doneStep" class="mt-6 flex flex-wrap items-center justify-between gap-3">
    <FinestButton
      v-if="canReturnTo(step - 1)"
      variant="ghost"
      :label="t('previous')"
      icon="pi pi-arrow-left"
      @click="goBackTo(step - 1)" />
    <Link v-else-if="!project" :href="ProjectsController.index()">
      <FinestButton variant="ghost" :label="t('cancel')" />
    </Link>
    <span v-else />

    <FinestButton
      v-if="!autoAdvancing"
      variant="primary"
      :label="screen === 'documents' ? t('submit') : t('next')"
      icon="pi pi-arrow-right"
      icon-pos="right"
      :blocked="!canLeaveStep"
      :disabled="form.processing || portfolioForm.processing"
      @click="next" />
  </div>

  <CompanyCreateDialog
    v-model:visible="showCompanyDialog"
    :show-trigger="false"
    return-to="financing_request_borrower"
    @created="selectCreatedCompany" />
</template>

<i18n>
{
  "de": {
    "eyebrow": "Finanzierungen · Anfrage erstellen",
    "heading": "Neue Finanzierungsanfrage",
    "subtitle": "Wir legen die Anfrage Schritt für Schritt an. Du kannst jederzeit zurück – gespeichert wird erst, wenn du sie im letzten Schritt einreichst.",
    "back": "Zu den Anfragen",
    "next": "Weiter",
    "previous": "Zurück",
    "cancel": "Abbrechen",
    "submit": "Anfrage einreichen",
    "loadingDocuments": "Pflichtdokumente werden geladen …",
    "empty": "—",
    "steps": {
      "origin": "Vorhaben",
      "borrower": "Kreditnehmer",
      "type": "Objektart",
      "location": "Lage",
      "units": "Einheiten & Miete",
      "financing": "Finanzierung",
      "documents": "Dokumente",
      "done": "Fertig"
    },
    "summary": {
      "property": "Objekt",
      "type": "Objektart",
      "borrower": "Kreditnehmer"
    },
    "questions": {
      "living_space_size": "Was ist die Gesamtwohnfläche?",
      "business_space_size": "Was ist die Gesamtgewerbefläche?"
    },
    "fields": {
      "estate_object_type": "Objektart",
      "street": "Straße",
      "house_number": "Hausnummer",
      "zip_code": "PLZ",
      "city": "Ort",
      "state": "Bundesland",
      "living_space_size": "Wohnfläche",
      "business_space_size": "Gewerbefläche",
      "number_of_units": "Anzahl Einheiten",
      "actual_rent": "Aktuelle Mieteinnahmen",
      "purchase_price": "Kaufpreis",
      "equity": "Eigenkapital",
      "loan_type": "Darlehensart",
      "total_financing_amount": "Finanzierungssumme"
    }
  },
  "en": {
    "eyebrow": "Financing · Create request",
    "heading": "New financing request",
    "subtitle": "We create the request step by step. You can go back at any time – nothing is saved until you hand the request in on the last step.",
    "back": "Back to requests",
    "next": "Next",
    "previous": "Back",
    "cancel": "Cancel",
    "submit": "Submit request",
    "loadingDocuments": "Loading required documents …",
    "empty": "—",
    "steps": {
      "origin": "Purpose",
      "borrower": "Borrower",
      "type": "Property type",
      "location": "Location",
      "units": "Units & rent",
      "financing": "Financing",
      "documents": "Documents",
      "done": "Done"
    },
    "summary": {
      "property": "Property",
      "type": "Property type",
      "borrower": "Borrower"
    },
    "questions": {
      "living_space_size": "What is the total living space?",
      "business_space_size": "What is the total commercial space?"
    },
    "fields": {
      "estate_object_type": "Property type",
      "street": "Street",
      "house_number": "House number",
      "zip_code": "Postal code",
      "city": "City",
      "state": "Federal state",
      "living_space_size": "Living space",
      "business_space_size": "Commercial space",
      "number_of_units": "Number of units",
      "actual_rent": "Current rental income",
      "purchase_price": "Purchase price",
      "equity": "Equity",
      "loan_type": "Loan type",
      "total_financing_amount": "Financing amount"
    }
  }
}
</i18n>
